import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  User, Dumbbell, Apple, Moon, Droplets, Target, Trophy,
  Calendar, ShoppingCart, TrendingUp, Clock, RefreshCw,
  ArrowLeft, ChevronRight, Heart, AlertTriangle, CheckCircle,
  Sparkles, Flame, Activity
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;

const AthleteDetailPage = () => {
  const { athleteId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [nutritionDetail, setNutritionDetail] = useState(null);
  const [workoutHistory, setWorkoutHistory] = useState(null);
  const [recoveryHistory, setRecoveryHistory] = useState(null);
  const [auraBreakdown, setAuraBreakdown] = useState(null);

  useEffect(() => {
    if (athleteId) {
      fetchAllData();
    }
  }, [athleteId]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('aura_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [dashRes, nutritionRes, workoutRes, recoveryRes, auraRes] = await Promise.all([
        axios.get(`${API}/api/supporter-access/athlete/${athleteId}/full-dashboard`, { headers }),
        axios.get(`${API}/api/supporter-access/athlete/${athleteId}/nutrition-detail?days=7`, { headers }),
        axios.get(`${API}/api/supporter-access/athlete/${athleteId}/workout-history?days=14`, { headers }),
        axios.get(`${API}/api/supporter-access/athlete/${athleteId}/recovery-history?days=7`, { headers }),
        axios.get(`${API}/api/supporter-access/athlete/${athleteId}/aura-breakdown`, { headers })
      ]);
      
      setDashboardData(dashRes.data);
      setNutritionDetail(nutritionRes.data);
      setWorkoutHistory(workoutRes.data);
      setRecoveryHistory(recoveryRes.data);
      setAuraBreakdown(auraRes.data);
    } catch (error) {
      console.error('Failed to fetch athlete data:', error);
      toast.error('Failed to load athlete data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-aura-primary animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!dashboardData) {
    return (
      <DashboardLayout>
        <div className="text-center py-16">
          <p className="text-white">Unable to load athlete data</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">
            Back to Dashboard
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const { athlete, today_summary, weekly_summary, goals, grocery_list, meal_plan } = dashboardData;
  const nutrition = today_summary?.nutrition || {};
  const recovery = today_summary?.recovery || {};
  const hydration = today_summary?.hydration || {};

  // Calculate nutrition percentages
  const caloriePercent = nutrition.targets?.calories 
    ? Math.round((nutrition.consumed?.calories || 0) / nutrition.targets.calories * 100)
    : 0;
  const proteinPercent = nutrition.targets?.protein
    ? Math.round((nutrition.consumed?.protein || 0) / nutrition.targets.protein * 100)
    : 0;

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-24">
        {/* Header */}
        <div>
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-aura-text-muted mb-2">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-aura-primary/20 flex items-center justify-center">
              {athlete.picture ? (
                <img src={athlete.picture} alt={athlete.name} className="w-16 h-16 rounded-full object-cover" />
              ) : (
                <User className="w-8 h-8 text-aura-primary" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-heading font-bold text-white">{athlete.name}</h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge className="bg-aura-primary/20 text-aura-primary border-0">{athlete.sport}</Badge>
                <Badge className="bg-white/10 text-aura-text-muted border-0">Age {athlete.age}</Badge>
              </div>
            </div>
            <div className="ml-auto text-right">
              <p className="text-3xl font-bold text-aura-primary">{athlete.aura_score}</p>
              <p className="text-xs text-aura-text-muted">Aura Score</p>
              {athlete.current_streak > 0 && (
                <Badge className="bg-amber-500/20 text-amber-400 border-0 mt-1">
                  {athlete.current_streak} day streak
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Today's Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Nutrition */}
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <Apple className="w-5 h-5 text-green-400" />
                <span className={`text-sm font-bold ${caloriePercent >= 80 ? 'text-green-400' : caloriePercent >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                  {caloriePercent}%
                </span>
              </div>
              <p className="text-white font-bold">{nutrition.consumed?.calories || 0}</p>
              <p className="text-xs text-aura-text-muted">of {nutrition.targets?.calories || 0} cal</p>
              <Progress value={Math.min(caloriePercent, 100)} className="mt-2 h-1" />
            </CardContent>
          </Card>

          {/* Protein */}
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <Dumbbell className="w-5 h-5 text-purple-400" />
                <span className={`text-sm font-bold ${proteinPercent >= 80 ? 'text-green-400' : proteinPercent >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                  {proteinPercent}%
                </span>
              </div>
              <p className="text-white font-bold">{nutrition.consumed?.protein || 0}g</p>
              <p className="text-xs text-aura-text-muted">of {nutrition.targets?.protein || 0}g protein</p>
              <Progress value={Math.min(proteinPercent, 100)} className="mt-2 h-1" />
            </CardContent>
          </Card>

          {/* Hydration */}
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <Droplets className="w-5 h-5 text-blue-400" />
                <span className="text-sm font-bold text-blue-400">
                  {Math.round((hydration.consumed_oz || 0) / (hydration.target_oz || 100) * 100)}%
                </span>
              </div>
              <p className="text-white font-bold">{hydration.consumed_oz || 0}oz</p>
              <p className="text-xs text-aura-text-muted">of {hydration.target_oz || 100}oz water</p>
              <Progress value={Math.min((hydration.consumed_oz || 0) / (hydration.target_oz || 100) * 100, 100)} className="mt-2 h-1" />
            </CardContent>
          </Card>

          {/* Recovery */}
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <Heart className="w-5 h-5 text-red-400" />
                {recovery.score && (
                  <span className={`text-sm font-bold ${recovery.score >= 70 ? 'text-green-400' : recovery.score >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                    {recovery.score}%
                  </span>
                )}
              </div>
              <p className="text-white font-bold">{recovery.sleep_hours || '--'}h sleep</p>
              <p className="text-xs text-aura-text-muted">
                {recovery.date ? format(parseISO(recovery.date + 'T00:00:00'), 'MMM d') : 'No recent log'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Weekly Progress */}
        <Card className="bg-aura-surface border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-aura-text-muted text-sm">Weekly Workouts</p>
                <p className="text-2xl font-bold text-white">
                  {weekly_summary?.workouts_completed || 0} / {weekly_summary?.workouts_target || 5}
                </p>
              </div>
              <div className="text-right">
                <p className="text-aura-text-muted text-sm">Total Time</p>
                <p className="text-2xl font-bold text-white">
                  {Math.round((weekly_summary?.total_workout_minutes || 0) / 60)}h
                </p>
              </div>
            </div>
            <Progress 
              value={(weekly_summary?.workouts_completed || 0) / (weekly_summary?.workouts_target || 5) * 100} 
              className="mt-3 h-2" 
            />
          </CardContent>
        </Card>

        {/* Tabs for Detailed Views */}
        <Tabs defaultValue="nutrition" className="w-full">
          <TabsList className="bg-aura-surface border border-white/10 w-full justify-start overflow-x-auto">
            <TabsTrigger value="nutrition" className="data-[state=active]:bg-aura-primary">
              <Apple className="w-4 h-4 mr-1" /> Nutrition
            </TabsTrigger>
            <TabsTrigger value="workouts" className="data-[state=active]:bg-aura-primary">
              <Dumbbell className="w-4 h-4 mr-1" /> Workouts
            </TabsTrigger>
            <TabsTrigger value="recovery" className="data-[state=active]:bg-aura-primary">
              <Moon className="w-4 h-4 mr-1" /> Recovery
            </TabsTrigger>
            <TabsTrigger value="goals" className="data-[state=active]:bg-aura-primary">
              <Target className="w-4 h-4 mr-1" /> Goals
            </TabsTrigger>
            <TabsTrigger value="aura" className="data-[state=active]:bg-aura-primary">
              <Sparkles className="w-4 h-4 mr-1" /> Aura
            </TabsTrigger>
          </TabsList>

          {/* Nutrition Tab */}
          <TabsContent value="nutrition" className="mt-4 space-y-4">
            <Card className="bg-aura-surface border-white/10">
              <CardHeader>
                <CardTitle className="text-white font-heading uppercase tracking-wider text-sm">
                  Nutrition This Week ({nutritionDetail?.total_meals_logged || 0} meals logged)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {nutritionDetail?.nutrition_by_day && Object.keys(nutritionDetail.nutrition_by_day).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(nutritionDetail.nutrition_by_day).slice(0, 7).map(([date, data]) => (
                      <div key={date} className="p-3 rounded-lg bg-white/5 border border-white/10">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-white font-medium">{format(parseISO(date + 'T00:00:00'), 'EEEE, MMM d')}</p>
                          <Badge className="bg-white/10 text-aura-text-muted">{data.meals?.length || 0} meals</Badge>
                        </div>
                        <div className="grid grid-cols-4 gap-2 text-center">
                          <div>
                            <p className="text-lg font-bold text-white">{data.totals?.calories || 0}</p>
                            <p className="text-xs text-aura-text-muted">cal</p>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-purple-400">{data.totals?.protein || 0}g</p>
                            <p className="text-xs text-aura-text-muted">protein</p>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-amber-400">{data.totals?.carbs || 0}g</p>
                            <p className="text-xs text-aura-text-muted">carbs</p>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-green-400">{data.totals?.fat || 0}g</p>
                            <p className="text-xs text-aura-text-muted">fat</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-aura-text-muted py-8">No nutrition logs this week</p>
                )}
              </CardContent>
            </Card>

            {/* Meal Plan & Grocery */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="bg-aura-surface border-white/10">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Calendar className={`w-8 h-8 ${meal_plan?.has_plan ? 'text-green-400' : 'text-aura-text-muted'}`} />
                    <div>
                      <p className="text-white font-medium">Meal Plan</p>
                      <p className="text-xs text-aura-text-muted">
                        {meal_plan?.has_plan ? 'Active' : 'Not created'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card 
                className="bg-aura-surface border-white/10 cursor-pointer hover:border-white/20 transition-all"
                onClick={() => navigate(`/supporter/athlete/${athleteId}/grocery`)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <ShoppingCart className={`w-8 h-8 ${grocery_list?.has_list ? 'text-green-400' : 'text-aura-text-muted'}`} />
                    <div className="flex-1">
                      <p className="text-white font-medium">Grocery List</p>
                      <p className="text-xs text-aura-text-muted">
                        {grocery_list?.has_list ? `${grocery_list.total_items} items` : 'Not created'}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-aura-text-muted" />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Workouts Tab */}
          <TabsContent value="workouts" className="mt-4 space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <Card className="bg-aura-surface border-white/10">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-white">{workoutHistory?.total_workouts || 0}</p>
                  <p className="text-xs text-aura-text-muted">Total Workouts</p>
                </CardContent>
              </Card>
              <Card className="bg-aura-surface border-white/10">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-aura-primary">{workoutHistory?.in_app_workouts || 0}</p>
                  <p className="text-xs text-aura-text-muted">In-App</p>
                </CardContent>
              </Card>
              <Card className="bg-aura-surface border-white/10">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-purple-400">{workoutHistory?.external_workouts || 0}</p>
                  <p className="text-xs text-aura-text-muted">External</p>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-aura-surface border-white/10">
              <CardHeader>
                <CardTitle className="text-white font-heading uppercase tracking-wider text-sm">
                  Recent Workouts
                </CardTitle>
              </CardHeader>
              <CardContent>
                {workoutHistory?.workouts?.length > 0 ? (
                  <div className="space-y-2">
                    {workoutHistory.workouts.slice(0, 10).map((workout, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${workout.type === 'external' ? 'bg-purple-500/20' : 'bg-aura-primary/20'}`}>
                            <Dumbbell className={`w-4 h-4 ${workout.type === 'external' ? 'text-purple-400' : 'text-aura-primary'}`} />
                          </div>
                          <div>
                            <p className="text-white font-medium">{workout.name || 'Workout'}</p>
                            <p className="text-xs text-aura-text-muted">
                              {format(parseISO(workout.date + 'T00:00:00'), 'MMM d')} • {workout.duration_minutes || 0} min
                            </p>
                          </div>
                        </div>
                        <Badge className={workout.type === 'external' ? 'bg-purple-500/20 text-purple-400' : 'bg-aura-primary/20 text-aura-primary'}>
                          {workout.type === 'external' ? 'External' : 'In-App'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-aura-text-muted py-8">No recent workouts</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Recovery Tab */}
          <TabsContent value="recovery" className="mt-4 space-y-4">
            {recoveryHistory?.averages && (
              <div className="grid grid-cols-2 gap-4">
                <Card className="bg-aura-surface border-white/10">
                  <CardContent className="p-4 text-center">
                    <Heart className="w-6 h-6 text-red-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{recoveryHistory.averages.recovery_score || '--'}</p>
                    <p className="text-xs text-aura-text-muted">Avg Recovery Score</p>
                  </CardContent>
                </Card>
                <Card className="bg-aura-surface border-white/10">
                  <CardContent className="p-4 text-center">
                    <Moon className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{recoveryHistory.averages.sleep_hours || '--'}h</p>
                    <p className="text-xs text-aura-text-muted">Avg Sleep</p>
                  </CardContent>
                </Card>
              </div>
            )}

            <Card className="bg-aura-surface border-white/10">
              <CardHeader>
                <CardTitle className="text-white font-heading uppercase tracking-wider text-sm">
                  Recovery History ({recoveryHistory?.days_logged || 0} days logged)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {recoveryHistory?.recovery_logs?.length > 0 ? (
                  <div className="space-y-2">
                    {recoveryHistory.recovery_logs.map((log, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                        <div>
                          <p className="text-white font-medium">{format(parseISO(log.date + 'T00:00:00'), 'EEEE, MMM d')}</p>
                          <p className="text-xs text-aura-text-muted">
                            {log.sleep_hours}h sleep • Fatigue: {log.fatigue_level}/10
                          </p>
                        </div>
                        <div className={`text-lg font-bold ${log.recovery_score >= 70 ? 'text-green-400' : log.recovery_score >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                          {log.recovery_score}%
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-aura-text-muted py-8">No recovery logs this week</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Goals Tab */}
          <TabsContent value="goals" className="mt-4 space-y-4">
            <Card className="bg-aura-surface border-white/10">
              <CardHeader>
                <CardTitle className="text-white font-heading uppercase tracking-wider text-sm">
                  Weight Goal Progress
                </CardTitle>
              </CardHeader>
              <CardContent>
                {goals?.has_goals ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-aura-text-muted text-sm">Current Goal</p>
                        <p className="text-white font-bold capitalize">{goals.weight_goal?.replace('_', ' ')}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-aura-text-muted text-sm">Progress</p>
                        <p className="text-white font-bold">
                          {goals.current_weight} → {goals.target_weight} lbs
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Target className="w-12 h-12 text-aura-text-muted mx-auto mb-3" />
                    <p className="text-white">No goals set yet</p>
                    <p className="text-sm text-aura-text-muted mt-1">
                      Encourage your athlete to set up their goals
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Aura Breakdown Tab */}
          <TabsContent value="aura" className="mt-4 space-y-4">
            <Card className="bg-gradient-to-br from-aura-primary/20 to-aura-secondary/20 border-aura-primary/30">
              <CardContent className="p-6 text-center">
                <Sparkles className="w-12 h-12 text-aura-primary mx-auto mb-3" />
                <p className="text-4xl font-bold text-white">{auraBreakdown?.total_aura_score || 0}</p>
                <p className="text-aura-text-muted">Total Aura Score</p>
                {auraBreakdown?.current_streak > 0 && (
                  <Badge className="bg-amber-500/20 text-amber-400 border-0 mt-2">
                    {auraBreakdown.current_streak} day streak
                  </Badge>
                )}
              </CardContent>
            </Card>

            <Card className="bg-aura-surface border-white/10">
              <CardHeader>
                <CardTitle className="text-white font-heading uppercase tracking-wider text-sm">
                  This Week's Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg bg-white/5 text-center">
                    <Dumbbell className="w-5 h-5 text-aura-primary mx-auto mb-1" />
                    <p className="text-xl font-bold text-white">{auraBreakdown?.breakdown?.workouts_this_week || 0}</p>
                    <p className="text-xs text-aura-text-muted">Workouts</p>
                  </div>
                  <div className="p-3 rounded-lg bg-white/5 text-center">
                    <Apple className="w-5 h-5 text-green-400 mx-auto mb-1" />
                    <p className="text-xl font-bold text-white">{auraBreakdown?.breakdown?.nutrition_logs_this_week || 0}</p>
                    <p className="text-xs text-aura-text-muted">Meals Logged</p>
                  </div>
                  <div className="p-3 rounded-lg bg-white/5 text-center">
                    <Moon className="w-5 h-5 text-purple-400 mx-auto mb-1" />
                    <p className="text-xl font-bold text-white">{auraBreakdown?.breakdown?.recovery_logs_this_week || 0}</p>
                    <p className="text-xs text-aura-text-muted">Recovery Logs</p>
                  </div>
                  <div className="p-3 rounded-lg bg-white/5 text-center">
                    <Trophy className="w-5 h-5 text-amber-400 mx-auto mb-1" />
                    <p className="text-xl font-bold text-white">{auraBreakdown?.breakdown?.badges_earned || 0}</p>
                    <p className="text-xs text-aura-text-muted">Badges</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default AthleteDetailPage;
